import React from "react";
import { MarketingLayout, StartServiceButton } from "@/components/marketing/MarketingLayout";
import { PageHero } from "@/components/marketing/blocks";
import { useSeo } from "@/hooks/useSeo";

const SECTIONS = [
  {
    id: "process-serving",
    title: "Process Serving",
    items: [
      {
        q: "How much does a process server cost in Memphis?",
        a: "AR Process Service and Notary charges per address: Routine service is $95, Rush is $135, and 24-Hour Priority is $285. You'll know the price before we start. Skip tracing for hard-to-find people is quoted separately.",
      },
      {
        q: "How long does it take to serve court papers in Shelby County?",
        a: "It depends on the service level you choose. Routine, Rush, and 24-Hour Priority each have a set time for the first attempt, listed on our Process Serving page. Completion depends on when the person can be found and whether they are avoiding service.",
      },
      {
        q: "Can I serve court papers myself in Tennessee?",
        a: "No. A person who is a party to the case should not serve their own papers. A neutral adult who isn't involved, such as a professional process server, handles delivery and provides sworn proof to the court. Your attorney or the court clerk can confirm the rules for your case.",
      },
      {
        q: "Which courts do you serve papers for?",
        a: "We serve papers for Shelby County General Sessions, Circuit, Chancery, Juvenile, and Probate Courts, as well as cases filed elsewhere that need service in the Memphis area.",
      },
      {
        q: "What documents do you serve?",
        a: "We serve summonses and complaints, subpoenas, eviction notices and detainer warrants, divorce and family court papers, small claims, and other civil legal documents throughout Memphis and Shelby County.",
      },
      {
        q: "What is an affidavit of service?",
        a: "An affidavit of service is a sworn, notarized document showing when, where, how, and to whom papers were delivered. It's filed with the court as proof that the person was properly notified. Every completed job includes one, backed by GPS-stamped, time-stamped proof.",
      },
      {
        q: "What happens if the person avoids service or can't be found?",
        a: "We make multiple attempts at different times and locations. If needed, we use skip tracing to find a current address. If service still isn't possible, we provide a sworn record of every attempt so your attorney can ask the court about other service options.",
      },
      {
        q: "Can you serve papers for an out-of-state attorney or law firm?",
        a: "Yes. We serve Tennessee papers for attorneys and law firms anywhere in the country. Send the documents electronically, and we return a notarized affidavit of service with GPS and time-stamped proof.",
      },
      {
        q: "What areas do you serve?",
        a: "We serve Memphis and all of Shelby County, including Germantown, Bartlett, Collierville, Cordova, Millington, Arlington, and Lakeland.",
      },
      {
        q: "What can't a process server do?",
        a: "A process server must follow the law. We never force entry, enter locked or gated property without permission, pretend to be law enforcement, or threaten or hold anyone. We serve people at home, at work, or in public places at reasonable hours.",
      },
    ],
  },
  {
    id: "mobile-notary",
    title: "Mobile Notary",
    items: [
      {
        q: "How much does a mobile notary cost in Memphis?",
        a: "Mobile notary service includes a $35 travel fee plus the notarization fee listed on our Notary Services page. You'll know the total before your appointment.",
      },
      {
        q: "What is a mobile notary?",
        a: "A mobile notary comes to you at your home, office, hospital, nursing facility, or another location in Memphis and Shelby County. The notary verifies your identity, witnesses your signature, and notarizes your documents on the spot.",
      },
      {
        q: "What ID do I need for a notarization?",
        a: "Every signer needs a valid, unexpired government-issued photo ID, such as a driver's license, state ID card, U.S. passport, or military ID. The name on the ID should match the name on the document.",
      },
      {
        q: "Can a notary come to a hospital, nursing home, or hospice?",
        a: "Yes. We regularly notarize documents at hospitals, nursing homes, rehab centers, and hospice facilities, including powers of attorney and healthcare directives. The signer must be alert and able to understand what they are signing.",
      },
      {
        q: "Do you offer same-day or after-hours notary appointments?",
        a: "Yes, when availability allows. Call (901) 695-2500 to check same-day or after-hours availability.",
      },
      {
        q: "Do you handle real estate and loan signings?",
        a: "Yes. We handle real estate closings and loan document signings for title companies, lenders, and escrow firms, and we return the signed documents promptly.",
      },
      {
        q: "What if the signer doesn't have a valid ID?",
        a: "Tennessee law may allow a credible witness who personally knows the signer, and who has valid ID, to vouch for the signer under oath. Call us before your appointment so we can confirm whether this option works for your situation.",
      },
      {
        q: "Can a notary prepare documents or give legal advice?",
        a: "No. A notary can't draft legal documents, give legal advice, or choose which form you need.",
      },
      {
        q: "Can a notary refuse to notarize a document?",
        a: "Yes. A notary must decline if the signer has no acceptable ID, seems confused or pressured, the document is incomplete, or there are signs of fraud. These rules protect you and the people relying on your document.",
      },
    ],
  },
  {
    id: "working-with-us",
    title: "Working With Us",
    items: [
      {
        q: "How will I be notified of the status of my request and assignment?",
        a: "You'll get a confirmation as soon as your request is received. We notify you when your job is assigned, after each service attempt, and when it's complete. Your notarized affidavit of service and GPS-stamped proof are delivered by email and are available in your client portal. Questions anytime: (901) 695-2500 or admin@artadmin.net.",
      },
      {
        q: "How do I place an order?",
        a: "Click Start A Service, call (901) 695-2500, or email admin@artadmin.net.",
        order: true,
      },
    ],
  },
];

function slugify(q) {
  return q.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
}

export default function Faq() {
  const origin = "https://artadmin.net";
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: SECTIONS.flatMap((s) =>
      s.items.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      }))
    ),
  };

  useSeo({
    title: "FAQ: Process Serving & Mobile Notary in Memphis, TN | AR Process Service and Notary",
    description:
      "Answers about process serving and mobile notary services in Memphis and Shelby County: pricing, turnaround, courts, documents, ID requirements, and order status.",
    canonicalPath: "/faq",
    jsonLd: faqSchema,
  });

  return (
    <MarketingLayout>
      <PageHero
        overline="FAQ"
        title="Frequently Asked Questions"
        subtitle="Answers about process serving and mobile notary services in Memphis and Shelby County, Tennessee — pricing, turnaround, courts, documents, ID requirements, and how we keep you updated."
      />

      <section className="px-5 py-14 sm:px-8 lg:py-20" data-testid="faq-page">
        <div className="mx-auto max-w-3xl space-y-14">
          {SECTIONS.map((section) => (
            <div key={section.id} data-testid={`faq-section-${section.id}`}>
              <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                {section.title}
              </h2>
              <div className="mt-8 space-y-9">
                {section.items.map((it) => (
                  <div key={it.q} data-testid={`faq-item-${slugify(it.q)}`}>
                    <h3 className="font-display text-lg font-semibold text-slate-900 sm:text-xl">{it.q}</h3>
                    {it.order ? (
                      <div className="mt-3 space-y-4">
                        <p className="text-[15px] leading-relaxed text-slate-600 sm:text-base">
                          Click Start A Service, call{" "}
                          <a href="tel:+19016952500" className="font-semibold text-sky-700 hover:text-sky-800" data-testid="faq-phone-link">
                            (901) 695-2500
                          </a>
                          , or email{" "}
                          <a href="mailto:admin@artadmin.net" className="font-semibold text-sky-700 hover:text-sky-800" data-testid="faq-email-link">
                            admin@artadmin.net
                          </a>
                          .
                        </p>
                        <StartServiceButton testid="faq-start-service-cta" label="Start A Service" />
                      </div>
                    ) : (
                      <p className="mt-3 text-[15px] leading-relaxed text-slate-600 sm:text-base">{it.a}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </MarketingLayout>
  );
}
